import { createContext, useCallback, useContext, useRef, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import Modal from './Modal';
import Button from './Button';

interface ConfirmOptions {
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

type ConfirmFn = (message: string, options?: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within a ConfirmProvider');
  return ctx;
}

interface PendingConfirm extends ConfirmOptions {
  message: string;
  resolve: (value: boolean) => void;
}

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((message, options) => {
    return new Promise<boolean>(resolve => {
      resolverRef.current = resolve;
      setPending({ message, resolve, ...options });
    });
  }, []);

  function settle(value: boolean) {
    resolverRef.current?.(value);
    resolverRef.current = null;
    setPending(null);
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AnimatePresence>
        {pending && (
          <Modal
            title={pending.title ?? 'Confirm'}
            onClose={() => settle(false)}
            width={420}
            footer={
              <>
                <Button variant="outline" onClick={() => settle(false)}>{pending.cancelLabel ?? 'Cancel'}</Button>
                <Button variant={pending.danger ? 'danger' : 'primary'} onClick={() => settle(true)}>
                  {pending.confirmLabel ?? 'Confirm'}
                </Button>
              </>
            }
          >
            <p className="modal-desc" style={{ marginBottom: 0 }}>{pending.message}</p>
          </Modal>
        )}
      </AnimatePresence>
    </ConfirmContext.Provider>
  );
}
